import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { ChevronLeft, Search, Shield, Trash2, RotateCcw, Package, Hash, Calendar, DollarSign, AlertTriangle } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';

const STATUS_STYLES = {
  IN_STOCK: 'bg-emerald-100 text-emerald-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-orange-100 text-orange-700',
  RETURNED: 'bg-teal-100 text-teal-700',
  SCRAPPED: 'bg-red-100 text-red-600',
  RETURNED_TO_SUPPLIER: 'bg-cyan-100 text-cyan-700',
  WRITTEN_OFF: 'bg-slate-100 text-slate-500',
};

function fmtDate(d, tz = 'America/Los_Angeles') {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz });
  } catch { return d; }
}

function fmtCurrency(v) {
  return (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

import { useAuth } from '../contexts/AuthContext';

export default function SerialLifecycleView({ itemCode, onBack }) {
  const { systemSettings } = useAuth();
  const sCurrency = systemSettings?.system_currency || 'USD';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionSerial, setActionSerial] = useState(null);
  const [actionType, setActionType] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedSerials, setSelectedSerials] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(''); // 'hold', 'scrap', 'release-hold'
  const [serialSort, handleSerialSort] = useSortState('serial_number', 'asc');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/inventory/serials/by-item/${itemCode}/lifecycle`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load serial data');
    }
    setLoading(false);
  }, [itemCode]);

  useEffect(() => { loadData(); }, [loadData]);

  const submitAction = async () => {
    if (!actionSerial) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/api/inventory/serials/${actionSerial.serial_number}/${actionType}`, {
        reason: reason || `Action from serial lifecycle view`,
      });
      toast.success(r.data.message, { duration: 6000 });
      setActionSerial(null);
      setReason('');
      loadData();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Action failed'));
    }
    setSubmitting(false);
  };

  const toggleSerial = (sn) => {
    setSelectedSerials(prev => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  const toggleAllVisible = (filtered) => {
    const actionable = filtered.filter(s => !['SCRAPPED', 'RETURNED_TO_SUPPLIER', 'WRITTEN_OFF'].includes(s.status));
    if (actionable.every(s => selectedSerials.has(s.serial_number))) {
      setSelectedSerials(new Set());
    } else {
      setSelectedSerials(new Set(actionable.map(s => s.serial_number)));
    }
  };

  const submitBulkAction = async () => {
    if (selectedSerials.size === 0 || !bulkAction) return;
    setSubmitting(true);
    let success = 0, failed = 0;
    for (const sn of selectedSerials) {
      try {
        await api.post(`/api/inventory/serials/${sn}/${bulkAction}`, { reason: reason || `Bulk ${bulkAction}` });
        success++;
      } catch { failed++; }
    }
    toast.success(`Bulk ${bulkAction}: ${success} succeeded${failed ? `, ${failed} failed` : ''}`, { duration: 6000 });
    setSelectedSerials(new Set());
    setBulkAction('');
    setReason('');
    setSubmitting(false);
    loadData();
  };

  if (loading) return <div className="py-12 text-center text-sm text-muted-foreground">Loading serial lifecycle data...</div>;
  if (!data) return <div className="py-12 text-center text-sm text-muted-foreground">No data found.</div>;

  const summary = data.summary || {};
  const allStatuses = Object.keys(summary);

  // Filter serials
  let filtered = data.data || [];
  if (statusFilter !== 'all') {
    filtered = filtered.filter(s => s.status === statusFilter);
  }
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(s =>
      s.serial_number?.toLowerCase().includes(q) ||
      s.batch_number?.toLowerCase().includes(q) ||
      s.location_code?.toLowerCase().includes(q) ||
      s.issued_to?.toLowerCase().includes(q)
    );
  }

  // Determine if any serial has expired batch
  const now = new Date();

  return (
    <div className="space-y-4" data-testid="serial-lifecycle-view">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="serial-lifecycle-back">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-base font-bold">
            <Hash className="h-4 w-4 inline mr-1.5 text-primary" />
            Serial Lifecycle — {data.item_code}
          </h2>
          <p className="text-xs text-muted-foreground">{data.item_description} | {data.total} serial number(s) | Total value: ${fmtCurrency(data.total_value)}</p>
        </div>
        <Button size="sm" variant="outline" onClick={loadData}>Refresh</Button>
      </div>

      {/* Summary cards */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${statusFilter === 'all' ? 'border-primary bg-primary/10 ring-2 ring-offset-1 ring-primary/30 text-primary' : 'border-border text-muted-foreground hover:bg-accent/20'}`}
          data-testid="serial-filter-all">
          All <span className="font-bold ml-1">{data.total}</span>
        </button>
        {allStatuses.map(st => (
          <button key={st} onClick={() => setStatusFilter(st)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${statusFilter === st ? `ring-2 ring-offset-1 ring-primary/30 ${STATUS_STYLES[st] || 'bg-slate-100'}` : 'border-border text-muted-foreground hover:bg-accent/20'}`}
            data-testid={`serial-filter-${st}`}>
            {st.replace(/_/g, ' ')} <span className="font-bold ml-1">{summary[st]}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative w-72">
        <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search serial, batch, location..."
          className="pl-8 h-8 text-sm" data-testid="serial-lifecycle-search" />
      </div>

      {/* Bulk action bar */}
      {selectedSerials.size > 0 && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2" data-testid="bulk-action-bar">
          <span className="text-xs font-semibold text-primary">{selectedSerials.size} serial(s) selected</span>
          <div className="flex gap-1.5 ml-auto">
            <Button size="sm" variant="outline" onClick={() => { setBulkAction('hold'); setReason(''); }}
              className="h-6 text-[10px] border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="bulk-hold-btn">
              <Shield className="h-3 w-3 mr-0.5" /> Hold All
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkAction('release-hold'); setReason(''); }}
              className="h-6 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-release-btn">
              <RotateCcw className="h-3 w-3 mr-0.5" /> Release All
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkAction('scrap'); setReason(''); }}
              className="h-6 text-[10px] border-red-300 text-red-700 hover:bg-red-50" data-testid="bulk-scrap-btn">
              <Trash2 className="h-3 w-3 mr-0.5" /> Scrap All
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedSerials(new Set())}
              className="h-6 text-[10px]" data-testid="bulk-clear-btn">Clear</Button>
          </div>
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-xl">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No serial numbers matching this filter</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-xs" data-testid="serial-lifecycle-table">
            <thead><tr className="bg-muted/50 border-b">
              <th className="px-2 py-2 w-8">
                <input type="checkbox" className="h-3.5 w-3.5 rounded"
                  checked={filtered.filter(s => !['SCRAPPED', 'RETURNED_TO_SUPPLIER', 'WRITTEN_OFF'].includes(s.status)).length > 0 && filtered.filter(s => !['SCRAPPED', 'RETURNED_TO_SUPPLIER', 'WRITTEN_OFF'].includes(s.status)).every(s => selectedSerials.has(s.serial_number))}
                  onChange={() => toggleAllVisible(filtered)} data-testid="serial-select-all" />
              </th>
              <SortableTableHeader label="Serial Number" field="serial_number" sortState={serialSort} onSort={handleSerialSort} />
              <SortableTableHeader label="Batch #" field="batch_number" sortState={serialSort} onSort={handleSerialSort} />
              <SortableTableHeader label="Location" field="location_code" sortState={serialSort} onSort={handleSerialSort} />
              <SortableTableHeader label="Status" field="status" sortState={serialSort} onSort={handleSerialSort} />
              <SortableTableHeader label="Expiry Date" field="expiry_date" sortState={serialSort} onSort={handleSerialSort} />
              <SortableTableHeader label="Received" field="received_at" sortState={serialSort} onSort={handleSerialSort} />
              <SortableTableHeader label={`Unit Value (${sCurrency})`} field="unit_cost" sortState={serialSort} onSort={handleSerialSort} className="text-right" />
              <SortableTableHeader label="Issued To / Notes" field="issued_to" sortState={serialSort} onSort={handleSerialSort} />
              <th className="text-center px-3 py-2 font-semibold">Actions</th>
            </tr></thead>
            <tbody>
              {sortData(filtered, serialSort.field, serialSort.dir).map((s, i) => {
                const expDate = s.expiry_date ? new Date(s.expiry_date) : null;
                const isExpired = expDate && expDate <= now;
                const isExpiringSoon = expDate && !isExpired && (expDate - now) / (1000 * 60 * 60 * 24) <= 30;
                const daysLeft = expDate ? Math.ceil((expDate - now) / (1000 * 60 * 60 * 24)) : null;

                const canHold = s.status === 'IN_STOCK' || s.status === 'RETURNED';
                const canRelease = s.status === 'ON_HOLD';
                const canScrap = !['SCRAPPED', 'RETURNED_TO_SUPPLIER', 'WRITTEN_OFF'].includes(s.status);
                const isTerminal = ['SCRAPPED', 'RETURNED_TO_SUPPLIER', 'WRITTEN_OFF'].includes(s.status);

                return (
                  <tr key={s.serial_number || i}
                    className={`border-b transition-colors hover:bg-accent/20 ${isExpired && !isTerminal ? 'bg-red-50/50' : isExpiringSoon && !isTerminal ? 'bg-amber-50/30' : ''}`}
                    data-testid={`serial-lifecycle-row-${s.serial_number}`}>
                    <td className="px-2 py-2">
                      {!isTerminal && <input type="checkbox" className="h-3.5 w-3.5 rounded"
                        checked={selectedSerials.has(s.serial_number)}
                        onChange={() => toggleSerial(s.serial_number)}
                        data-testid={`serial-check-${s.serial_number}`} />}
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold text-primary">{s.serial_number}</td>
                    <td className="px-3 py-2 text-muted-foreground">{s.batch_number || '—'}</td>
                    <td className="px-3 py-2 font-semibold">{s.location_code}</td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[9px] ${STATUS_STYLES[s.status] || 'bg-slate-100'}`}>
                        {s.status?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {s.expiry_date ? (
                        <span className={isExpired ? 'text-red-700 font-bold' : isExpiringSoon ? 'text-amber-600 font-semibold' : ''}>
                          {fmtDate(s.expiry_date)}
                          {daysLeft !== null && (
                            <span className="text-muted-foreground ml-1 text-[9px]">
                              ({daysLeft <= 0 ? `${Math.abs(daysLeft)}d ago` : `${daysLeft}d`})
                            </span>
                          )}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                      {isExpired && !isTerminal && (
                        <AlertTriangle className="h-3 w-3 inline ml-1 text-red-500" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(s.received_date)}</td>
                    <td className="px-3 py-2 text-right font-semibold">${fmtCurrency(s.unit_cost)}</td>
                    <td className="px-3 py-2 text-muted-foreground max-w-[150px] truncate">
                      {s.issued_to || s.hold_reason || s.scrap_reason || s.notes || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <div className="flex gap-0.5 justify-center">
                        {canHold && (
                          <Button size="sm" variant="ghost"
                            className="h-5 text-[9px] px-1.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                            onClick={() => { setActionSerial(s); setActionType('hold'); setReason(''); }}
                            data-testid={`serial-hold-${s.serial_number}`}>
                            <Shield className="h-2.5 w-2.5 mr-0.5" /> Hold
                          </Button>
                        )}
                        {canRelease && (
                          <Button size="sm" variant="ghost"
                            className="h-5 text-[9px] px-1.5 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                            onClick={() => { setActionSerial(s); setActionType('release-hold'); setReason(''); }}
                            data-testid={`serial-release-${s.serial_number}`}>
                            <RotateCcw className="h-2.5 w-2.5 mr-0.5" /> Release
                          </Button>
                        )}
                        {canScrap && (
                          <Button size="sm" variant="ghost"
                            className="h-5 text-[9px] px-1.5 text-red-600 hover:text-red-800 hover:bg-red-50"
                            onClick={() => { setActionSerial(s); setActionType('scrap'); setReason(''); }}
                            data-testid={`serial-scrap-${s.serial_number}`}>
                            <Trash2 className="h-2.5 w-2.5 mr-0.5" /> Scrap
                          </Button>
                        )}
                        {isTerminal && <span className="text-[9px] text-muted-foreground italic">Disposed</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="border-t-2 font-semibold bg-muted/30">
              <td></td>
              <td colSpan={6} className="px-3 py-2 text-right">{filtered.length} serial(s)</td>
              <td className="px-3 py-2 text-right">${fmtCurrency(filtered.reduce((s, r) => s + (r.unit_cost || 0), 0))}</td>
              <td colSpan={2}></td>
            </tr></tfoot>
          </table>
        </div>
      )}

      {/* Action Dialog (Hold / Release / Scrap) */}
      {actionSerial && (
        <Dialog open onOpenChange={() => setActionSerial(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-sm">
                {actionType === 'hold' ? 'Place Serial On Hold' : actionType === 'release-hold' ? 'Release Serial From Hold' : 'Scrap Serial'}
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs bg-muted/50 rounded-lg p-3 space-y-1">
              <p><span className="text-muted-foreground">Serial:</span> <strong className="font-mono">{actionSerial.serial_number}</strong></p>
              <p><span className="text-muted-foreground">Item:</span> <strong>{actionSerial.item_code}</strong></p>
              <p><span className="text-muted-foreground">Batch:</span> {actionSerial.batch_number || '—'}</p>
              <p><span className="text-muted-foreground">Location:</span> {actionSerial.location_code}</p>
              <div className="flex items-center gap-1"><span className="text-muted-foreground">Status:</span> <Badge className={`text-[9px] ${STATUS_STYLES[actionSerial.status]}`}>{actionSerial.status}</Badge></div>
              {actionSerial.expiry_date && <p><span className="text-muted-foreground">Expiry:</span> <strong>{fmtDate(actionSerial.expiry_date)}</strong></p>}
              <p><span className="text-muted-foreground">Value:</span> ${fmtCurrency(actionSerial.unit_cost)}</p>
            </div>

            {actionType === 'scrap' && (
              <p className="text-xs text-red-600 bg-red-50 p-2 rounded">
                This will permanently remove the serial from stock. The record is preserved for audit trail.
              </p>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-semibold">Reason / Notes</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs"
                placeholder={actionType === 'hold' ? 'Quality inspection required...' : actionType === 'scrap' ? 'Damaged beyond repair, expired...' : 'QA approved, safe to return to stock...'}
                data-testid="serial-action-reason" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setActionSerial(null)}>Cancel</Button>
              <Button size="sm" onClick={submitAction} disabled={submitting}
                className={actionType === 'hold' ? 'bg-orange-600 hover:bg-orange-700' : actionType === 'scrap' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                data-testid="serial-action-confirm">
                {submitting ? 'Processing...' : actionType === 'hold' ? 'Confirm Hold' : actionType === 'scrap' ? 'Confirm Scrap' : 'Confirm Release'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Action Dialog */}
      {bulkAction && (
        <Dialog open onOpenChange={() => setBulkAction('')}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-sm">
                Bulk {bulkAction === 'hold' ? 'Hold' : bulkAction === 'scrap' ? 'Scrap' : 'Release'} — {selectedSerials.size} Serial(s)
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">
              {bulkAction === 'hold' && 'This will place all selected serials on hold, removing them from available stock.'}
              {bulkAction === 'scrap' && 'This will permanently scrap all selected serials. Records are preserved for audit trail.'}
              {bulkAction === 'release-hold' && 'This will release all selected serials from hold back to available stock.'}
            </p>
            {bulkAction === 'scrap' && (
              <p className="text-xs text-red-600 bg-red-50 p-2 rounded font-semibold">
                Warning: Scrapping {selectedSerials.size} serial(s) cannot be undone.
              </p>
            )}
            <div className="text-xs bg-muted/50 rounded-lg p-3 max-h-24 overflow-y-auto">
              {[...selectedSerials].slice(0, 10).map(sn => <span key={sn} className="font-mono inline-block mr-2 mb-1">{sn}</span>)}
              {selectedSerials.size > 10 && <span className="text-muted-foreground">...and {selectedSerials.size - 10} more</span>}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Reason / Notes</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs"
                placeholder={bulkAction === 'hold' ? 'Batch quality hold...' : bulkAction === 'scrap' ? 'Expired beyond recovery...' : 'QA approved...'}
                data-testid="bulk-action-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setBulkAction('')}>Cancel</Button>
              <Button size="sm" onClick={submitBulkAction} disabled={submitting}
                className={bulkAction === 'hold' ? 'bg-orange-600 hover:bg-orange-700' : bulkAction === 'scrap' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                data-testid="bulk-action-confirm">
                {submitting ? `Processing ${selectedSerials.size}...` : `Confirm ${bulkAction === 'hold' ? 'Hold' : bulkAction === 'scrap' ? 'Scrap' : 'Release'} (${selectedSerials.size})`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
